// src/poker/PokerClockPage.js
import React, { useMemo, useState } from "react";
import usePokerStorage from "./hooks/usePokerStorage";
import usePokerClock from "./hooks/usePokerClock";

import {
  buildDefaultLevels,
  presets,
  calcPrizePool,
  calcAvgStack,
  calcPayoutErrors,
  money,
} from "./pokerClock.logic";

import ClockPanel from "./components/ClockPanel";
import SetupPanel from "./components/SetupPanel";
import LevelsTable from "./components/LevelsTable";

export default function PokerClockPage() {
  const [settings, setSettings] = useState({
    title: "Turniej",
    players: 8,
    startingStack: 20000,
    buyin: 50,
    fee: 0,
    rebuyEnabled: true,
    rebuyChips: 20000,
    rebuyPrice: 50,
    rebuyUntilLevel: 6,
    reentryEnabled: false,
    reentryPrice: 50,
    payoutMode: "percent", // "percent" | "amount"
    payout: [
      { place: 1, value: 50 },
      { place: 2, value: 30 },
      { place: 3, value: 20 },
    ],
  });

  const [levels, setLevels] = useState(() => buildDefaultLevels(12));
  const [levelIndex, setLevelIndex] = useState(0);
  const [running, setRunning] = useState(false);
  const [remainingSec, setRemainingSec] = useState(levels?.[0]?.minutes ? levels[0].minutes * 60 : 0);

  const { loadedOnce, clearStorageAndReset } = usePokerStorage({
    settings,
    setSettings,
    levels,
    setLevels,
    levelIndex,
    setLevelIndex,
    remainingSec,
    setRemainingSec,
    running,
    setRunning,
  });

  const { selectLevel, goPrev, goNext, resetLevel, applyPreset } = usePokerClock({
    loadedOnce,
    levels,
    setLevels,
    levelIndex,
    setLevelIndex,
    running,
    setRunning,
    remainingSec,
    setRemainingSec,
  });

  const current = levels[levelIndex] || null;

  const prizePool = useMemo(() => calcPrizePool(settings), [settings]);
  const avgStack = useMemo(() => calcAvgStack(settings), [settings]);
  const payoutErrors = useMemo(() => calcPayoutErrors(settings, prizePool), [settings, prizePool]);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (e) {
      console.warn("Fullscreen error:", e);
    }
  };

  const setField = (key, val) => setSettings((s) => ({ ...s, [key]: val }));

  const updatePayoutRow = (i, patch) => {
    setSettings((s) => {
      const arr = Array.isArray(s.payout) ? [...s.payout] : [];
      arr[i] = { ...arr[i], ...patch };
      return { ...s, payout: arr };
    });
  };

  const addPayoutRow = () => {
    setSettings((s) => {
      const arr = Array.isArray(s.payout) ? [...s.payout] : [];
      const nextPlace = arr.length ? Math.max(...arr.map((x) => Number(x.place) || 0)) + 1 : 1;
      arr.push({ place: nextPlace, value: 0 });
      return { ...s, payout: arr };
    });
  };

  const removePayoutRow = (i) => {
    setSettings((s) => {
      const arr = Array.isArray(s.payout) ? [...s.payout] : [];
      arr.splice(i, 1);
      return { ...s, payout: arr };
    });
  };

  const updateLevel = (i, patch) => {
    setLevels((prev) => {
      const arr = [...prev];
      arr[i] = { ...arr[i], ...patch };
      return arr;
    });
  };

  const addLevel = () => {
    setLevels((prev) => {
      const last = prev[prev.length - 1] || { sb: 100, bb: 200, minutes: 12, ante: 0 };
      return [
        ...prev,
        {
          id: `L${prev.length + 1}`,
          label: `Level ${prev.length + 1}`,
          minutes: last.minutes,
          sb: last.sb,
          bb: last.bb,
          ante: last.ante || 0,
        },
      ];
    });
  };

  const removeLevel = (i) => {
    setRunning(false);
    setLevels((prev) => {
      const arr = [...prev];
      arr.splice(i, 1);
      return arr.length ? arr : buildDefaultLevels(12);
    });
    setLevelIndex((idx) => Math.max(0, Math.min(idx, levels.length - 2)));
  };

  const onOffPL = (v) => (v ? "WŁ." : "WYŁ.");

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        padding: "18px 12px 40px",
        boxSizing: "border-box",
      }}
    >
      <div
        className="glass-container"
        style={{
          width: "100%",
          maxWidth: 1200,
          padding: "16px clamp(12px, 3vw, 20px)",
          borderRadius: 18,
          fontFamily: "var(--font-main)",
          color: "var(--color-text)",
          boxSizing: "border-box",
        }}
      >
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <h1 style={{ margin: 0, fontSize: "clamp(1.2rem, 2.6vw, 1.8rem)" }}>🃏 Poker Clock</h1>

          <div style={{ marginLeft: "auto", display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="btn-primary" onClick={toggleFullscreen} type="button">
              Pełny ekran
            </button>
            <button
              className="btn-secondary"
              onClick={clearStorageAndReset}
              type="button"
              title="Czyści ustawienia i timer"
            >
              Reset
            </button>
          </div>
        </div>

        {/* Góra: zegar */}
        <div
          style={{
            marginTop: 14,
            display: "grid",
            gridTemplateColumns: "1.15fr 0.85fr",
            gap: 14,
          }}
        >
          <ClockPanel
            settings={settings}
            current={current}
            remainingSec={remainingSec}
            running={running}
            prizePool={prizePool}
            onPrev={goPrev}
            onNext={goNext}
            onToggleRunning={() => setRunning((r) => !r)}
            onResetLevel={resetLevel}
          />

          {/* Prawa: presety + szybkie info */}
          <div className="panel-card">
            <div style={{ fontWeight: 700, marginBottom: 10 }}>Presety struktury</div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {presets.map((p) => (
                <button key={p.key} className="btn-secondary" type="button" onClick={() => applyPreset(p.minutes)}>
                  {p.name}
                </button>
              ))}
            </div>

            <div style={{ height: 12 }} />

            <div style={{ fontSize: 13, opacity: 0.9, lineHeight: 1.5 }}>
              <div>
                Śr. stack: <b>{money(avgStack)}</b>
              </div>
              <div>
                Rebuy: <b>{onOffPL(!!settings.rebuyEnabled)}</b>{" "}
                {settings.rebuyEnabled ? (
                  <>
                    · do poziomu <b>{settings.rebuyUntilLevel}</b> · żetony <b>{money(settings.rebuyChips)}</b>
                  </>
                ) : null}
              </div>
              <div>
                Re-entry: <b>{onOffPL(!!settings.reentryEnabled)}</b>
              </div>
            </div>

            <div style={{ height: 12 }} />

            <div style={{ fontWeight: 700, marginBottom: 6 }}>Payout</div>
            {payoutErrors.length ? (
              <div style={{ fontSize: 12, color: "var(--color-accent)", opacity: 0.95 }}>
                {payoutErrors.map((e, idx) => (
                  <div key={idx}>⚠ {e}</div>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: 12, opacity: 0.85 }}>OK</div>
            )}

            <div style={{ marginTop: 8, fontSize: 13 }}>
              {settings.payoutMode === "percent" ? (
                <div style={{ opacity: 0.9 }}>Tryb: procenty</div>
              ) : (
                <div style={{ opacity: 0.9 }}>Tryb: kwoty</div>
              )}
            </div>
          </div>
        </div>

        {/* Dół: ustawienia + tabela leveli */}
        <div style={{ marginTop: 14 }} className="poker-bottom-grid">
          <SetupPanel
            settings={settings}
            setField={setField}
            updatePayoutRow={updatePayoutRow}
            addPayoutRow={addPayoutRow}
            removePayoutRow={removePayoutRow}
            prizePool={prizePool}
            payoutErrors={payoutErrors}
          />

          <LevelsTable
            levels={levels}
            levelIndex={levelIndex}
            onAddLevel={addLevel}
            onRemoveLevel={removeLevel}
            onSelectLevel={selectLevel}
            onUpdateLevel={updateLevel}
          />
        </div>

        {/* Minimalne style input + wspólne karty paneli */}
        <style>{`
          .input{
            width:100%;
            box-sizing:border-box;
            padding:8px 10px;
            border-radius:12px;
            border:1px solid rgba(255,255,255,0.18);
            background:rgba(0,0,0,0.10);
            color:var(--color-text);
            outline:none;
          }
          .input:focus{ border-color: rgba(255,165,0,0.9); }

          .panel-card{
            border: 1px solid rgba(255, 165, 0, 0.55);
            border-radius: 16px;
            padding: 14px;
            box-shadow:
              0 0 0 1px rgba(255,165,0,0.10) inset,
              0 0 22px rgba(255,165,0,0.10);
          }

          .poker-bottom-grid{
            display:grid;
            grid-template-columns: 1fr 1.2fr;
            gap:14px;
          }
          @media (max-width: 900px){
            .poker-bottom-grid{
              grid-template-columns: 1fr;
            }
          }
        `}</style>
      </div>
    </div>
  );
}
